/**
 * @web3-react/injected-connector uses legacy ethereum.send(), which MetaMask's
 * multi-wallet window.ethereum proxy often rejects with "No active wallet found"
 * even when unlocked. Route those calls through EIP-1193 request(), and prefer
 * MetaMask when several extensions share window.ethereum.providers.
 */
type Ethereumish = {
  request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  send?: (...args: unknown[]) => unknown;
  providers?: Ethereumish[];
  setSelectedProvider?: (p: Ethereumish) => void;
  isMetaMask?: boolean;
  __web3ReactSendShim?: boolean;
};

function trySelectPreferredProvider(eth: Ethereumish): void {
  const providers = eth.providers;
  if (
    !Array.isArray(providers) ||
    providers.length < 2 ||
    typeof eth.setSelectedProvider !== "function"
  ) {
    return;
  }
  const metamask = providers.find(
    (p) =>
      p.isMetaMask &&
      !(p as { isRabby?: boolean }).isRabby &&
      !(p as { isCoinbaseWallet?: boolean }).isCoinbaseWallet &&
      !(p as { isBraveWallet?: boolean }).isBraveWallet
  );
  const pick = metamask ?? providers[0];
  try {
    eth.setSelectedProvider(pick);
  } catch {
    /* ignore */
  }
}

function shimSendToRequest(eth: Ethereumish): void {
  if (
    typeof eth.request !== "function" ||
    typeof eth.send !== "function" ||
    eth.__web3ReactSendShim
  ) {
    return;
  }
  const origSend = eth.send.bind(eth);
  eth.__web3ReactSendShim = true;
  eth.send = function (
    this: Ethereumish,
    payload: unknown,
    ...rest: unknown[]
  ): unknown {
    if (typeof rest[0] === "function") {
      return (origSend as (a: unknown, b: unknown) => unknown)(payload, rest[0]);
    }
    if (typeof payload === "string") {
      const params = Array.isArray(rest[0]) ? (rest[0] as unknown[]) : [];
      return this.request!({ method: payload, params });
    }
    if (
      payload &&
      typeof payload === "object" &&
      typeof (payload as { method?: unknown }).method === "string"
    ) {
      return this.request!(payload as { method: string; params?: unknown[] });
    }
    return (origSend as (...a: unknown[]) => unknown)(payload, ...rest);
  };
}

export function stabilizeInjectedEthereum(): void {
  if (typeof window === "undefined") {
    return;
  }
  const eth = (window as unknown as { ethereum?: Ethereumish }).ethereum;
  if (!eth) {
    return;
  }
  trySelectPreferredProvider(eth);
  shimSendToRequest(eth);
}
